import { transactionsCountLimit } from '@carboncopyco/editor-utils';
import { InfoIcon } from '@carboncopyco/icons';
import { MessageBox } from '@carboncopyco/ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';
import { RuleListProvider } from '../../../RulesManager';
import type { Transaction } from '../../types';
import { TransactionListItem } from './TransactionListItem';

export interface TransactionListProps {
  transactions: Transaction[];
  showLimitMessage?: boolean;
  searchValue?: string;
  selectedTransaction?: Transaction;
  onSelectTransaction?: (transaction: Transaction) => void;
}

export const TransactionList = ({
  transactions,
  showLimitMessage,
  searchValue,
  selectedTransaction,
  onSelectTransaction,
}: TransactionListProps) => {
  const [openedTransactions, setOpenedTransactions] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>();
  const rowVirtualizer = useVirtualizer({
    count: transactions.length,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) =>
      openedTransactions.includes(transactions[index].id) ? 400 : 60,
    overscan: 10,
  });

  useEffect(() => {
    rowVirtualizer.measure();
    if (openedTransactions.length) {
      rowVirtualizer.scrollToIndex(
        transactions.findIndex(
          (transaction) => transaction.id === openedTransactions[0]
        ),
        { behavior: 'smooth' }
      );
    }
  }, [transactions, openedTransactions]);

  return (
    <div className="flex-1 flex flex-col gap-2.5">
      {showLimitMessage && (
        <MessageBox
          border
          leftIcon={<InfoIcon />}
          label={`Due to the limit of ${transactionsCountLimit}, newer transactions are displayed in place of older ones`}
        />
      )}
      <RuleListProvider
        rules={transactions.map(({ id, ...transaction }) => ({
          id,
          rule: transaction,
        }))}
        openedRules={openedTransactions}
        onOpenRule={(id) => setOpenedTransactions([id])}
        onCloseRule={(id) =>
          setOpenedTransactions((openIds) =>
            openIds.filter((openId) => openId !== id)
          )
        }
      >
        <div
          ref={containerRef}
          className="relative overflow-y-scroll h-full"
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => {
            const transaction = transactions[virtualItem.index];
            return (
              <div
                key={virtualItem.key}
                className="absolute top-0 left-0 w-full"
                style={{
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <TransactionListItem
                  searchValue={searchValue}
                  transaction={transaction}
                  open={openedTransactions.includes(transaction.id)}
                  selected={selectedTransaction?.id === transaction.id}
                  onClick={() => onSelectTransaction?.(transaction)}
                />
              </div>
            );
          })}
        </div>
      </RuleListProvider>
    </div>
  );
};
